import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';
import {
  FeelIcon, NeedIcon, GoldIcon,
  SaveIcon, ChapterIcon, ArrowRightIcon, ArrowLeftIcon, InsightIcon,
  ExpandIcon, CollapseIcon, ResetIcon, DownloadIcon, SparkleIcon,
  ArrowDownIcon, PartnerIcon, ObserveIcon, TimerIcon
} from '../icons/ElchmeIcons';

// ElchmE Branding Component - Multicolor letters
const ElchmEBrand = ({ size = 'lg', showSubtitle = true }) => {
  const sizeClasses = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-3xl',
    xl: 'text-4xl'
  };
  
  return (
    <a 
      href="https://elchme.com" 
      target="_blank" 
      rel="noopener noreferrer"
      className="group inline-block"
      data-testid="elchme-brand-link"
    >
      <span className={`font-light tracking-wide ${sizeClasses[size]}`}>
        <span className="text-blue-400 group-hover:text-blue-300 transition-colors">El</span>
        <span className="text-rose-400 group-hover:text-rose-300 transition-colors">ch</span>
        <span className="text-emerald-400 group-hover:text-emerald-300 transition-colors">m</span>
        <span className="text-amber-400 group-hover:text-amber-300 transition-colors">E</span>
      </span>
      {showSubtitle && (
        <span className="block text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
          Empathetic Alchemy Platform
        </span>
      )}
    </a>
  );
};

// ElchmE Callout Card Component for Fishbowl
const ElchmEFishbowlCallout = () => (
  <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-2 border-teal-500/30 rounded-2xl p-6 relative overflow-hidden">
    {/* Animated background elements */}
    <div className="absolute top-0 left-0 w-24 h-24 bg-teal-500/10 rounded-full blur-2xl animate-pulse" />
    <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
    
    <div className="relative z-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <SparkleIcon size={28} color="#14B8A6" />
            <span className="text-teal-300 text-sm font-medium">EXPANDED VERSION</span>
          </div>
          <ElchmEBrand size="lg" />
          <p className="text-slate-300 mt-3 max-w-md">
            Experience the full <span className="text-teal-300 font-semibold">Empathetic Alchemy</span> process with our interactive platform — 
            guided fishbowl sessions, live timers, and collaborative tools for deeper connection.
          </p>
        </div>
        
        <a 
          href="https://elchme.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105 shadow-lg shadow-teal-500/20"
          data-testid="elchme-fishbowl-cta"
        >
          <span>Explore ElchmE.com</span>
          <ArrowRightIcon size={20} />
        </a>
      </div>
      
      {/* Features row */}
      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-slate-700/50">
        <span className="px-3 py-1 bg-teal-500/20 text-teal-300 rounded-full text-xs">Fishbowl Sessions</span>
        <span className="px-3 py-1 bg-rose-500/20 text-rose-300 rounded-full text-xs">Live Timer</span>
        <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs">Witness Support</span>
        <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs">The Gold Discovery</span>
        <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">Celebration</span>
      </div>
    </div>
  </div>
);

// Process Steps with timing guidelines
const processSteps = [
  {
    id: 'setup',
    title: 'Setup the Fishbowl',
    duration: '2-5 min',
    description: 'Two people sit in the center. Supportive witnesses gather around the perimeter.',
    icon: '🐟',
    color: 'blue'
  },
  {
    id: 'observations',
    title: 'Observations Without Judgment',
    duration: '5-10 min',
    description: 'Share what happened factually. No interpretations, no blame. Pure observation.',
    icon: '👁️',
    color: 'slate'
  },
  {
    id: 'needs',
    title: 'The Green: Needs Discovery',
    duration: '10-15 min',
    description: 'Both parties explore and name their underlying needs. What matters most?',
    icon: '🌱',
    color: 'emerald'
  },
  {
    id: 'feelings',
    title: 'The Red: Feelings Expression',
    duration: '10-15 min',
    description: 'Express feelings when needs are met and unmet. Stay in vulnerability.',
    icon: '❤️',
    color: 'rose'
  },
  {
    id: 'strategies',
    title: 'The Gold: Mutual Strategies',
    duration: '10-20 min',
    description: 'Ascend to strategies and requests that mutually benefit. Find the minimum solution.',
    icon: '🏆',
    color: 'amber'
  },
  {
    id: 'closure',
    title: 'Celebration & Closure',
    duration: '5-10 min',
    description: 'Acknowledge the courage shown. Celebrate the connection. Express gratitude.',
    icon: '✨',
    color: 'purple'
  }
];

// Alchemy Insights
const alchemyInsights = [
  {
    id: 'vulnerability',
    title: 'Vulnerability → Potency',
    content: 'Stay in the love and stay in the vulnerability. The vulnerability leads to potency paradoxically! This is where transformation happens.',
    icon: '💪'
  },
  {
    id: 'receptivity',
    title: 'Change Yourself First',
    content: "Don't make people do anything. Don't wait for them to be a certain way. Change yourself to your receptivity of love and understanding firstly, and then delineate your needs.",
    icon: '🪞'
  },
  {
    id: 'tenderness',
    title: 'Universal Tenderness',
    content: 'Return observations without judgment starting with mutual universal tenderness. We calmly discuss our needs.',
    icon: '🕊️'
  },
  {
    id: 'alchemy',
    title: 'The Alchemy',
    content: 'The red of the feelings inside of us, combined with the green of our needs, results in the alchemy to Gold — strategies that heal, strategies that work, strategies that bind us together.',
    icon: '⚗️'
  }
];

export default function EmpatheticAlchemy() {
  const navigate = useNavigate();
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);

  // Session data
  const [sessionData, setSessionData] = useState(() => {
    const saved = localStorage.getItem('empatheticAlchemyData');
    return saved ? JSON.parse(saved) : {
      participants: { person1: '', person2: '' },
      witnesses: '',
      observations: { person1: '', person2: '' },
      needs: { person1: ['', '', ''], person2: ['', '', ''] },
      feelings: {
        person1: { met: '', unmet: '' },
        person2: { met: '', unmet: '' }
      },
      strategies: { person1: '', person2: '', mutual: '' },
      celebration: '',
      insights: ''
    };
  });

  const sections = [
    { id: 'intro', title: 'Empathetic Alchemy', page: 26 },
    { id: 'setup', title: 'Fishbowl Setup', page: 26 },
    { id: 'process', title: 'The Process', page: 26 },
    { id: 'session', title: 'Live Session', page: 26 },
    { id: 'closure', title: 'Celebration', page: 26 },
  ];

  const progress_pct = ((currentSection + 1) / sections.length) * 100;

  // Timer effect
  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setTimerSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Save progress
  const saveProgress = () => {
    localStorage.setItem('empatheticAlchemyData', JSON.stringify(sessionData));
    toast.success('Session saved locally!');
  };

  const resetAll = () => {
    if (window.confirm('Reset all session data? This cannot be undone.')) {
      setSessionData({
        participants: { person1: '', person2: '' },
        witnesses: '',
        observations: { person1: '', person2: '' },
        needs: { person1: ['', '', ''], person2: ['', '', ''] },
        feelings: {
          person1: { met: '', unmet: '' },
          person2: { met: '', unmet: '' }
        },
        strategies: { person1: '', person2: '', mutual: '' },
        celebration: '',
        insights: ''
      });
      localStorage.removeItem('empatheticAlchemyData');
      setTimerSeconds(0);
      setTimerRunning(false);
      toast.success('Session data reset!');
    }
  };

  // Download as PDF
  const downloadPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to download the PDF');
      return;
    }
    
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Empathetic Alchemy Session</title>
  <style>
    body { font-family: Georgia, serif; line-height: 1.6; color: #1a1a1a; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #8B5CF6; }
    .header h1 { font-size: 28px; color: #8B5CF6; margin-bottom: 8px; }
    .section { margin: 25px 0; page-break-inside: avoid; }
    .section h2 { font-size: 18px; color: #2d5a3d; border-bottom: 2px solid #2d5a3d; padding-bottom: 8px; margin-bottom: 15px; }
    .participant { background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 8px; padding: 15px; margin: 10px 0; }
    .participant.p1 { border-color: #22C55E; }
    .participant.p2 { border-color: #8B5CF6; }
    .label { font-weight: bold; color: #555; font-size: 13px; margin-bottom: 5px; }
    .value { background: white; border: 1px solid #ddd; border-radius: 4px; padding: 10px; margin-bottom: 10px; min-height: 25px; }
    .value.empty { color: #999; font-style: italic; }
    .gold { background: linear-gradient(135deg, #FEF3C7, #FDE68A); border: 2px solid #F59E0B; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .gold h3 { color: #D97706; margin-bottom: 10px; }
    .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #888; border-top: 1px solid #ddd; padding-top: 20px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>⚗️ Empathetic Alchemy Session</h1>
    <div>Belonging Sessions • Fishbowl Process</div>
    <div style="margin-top: 10px; color: #666;">${currentDate}</div>
  </div>

  <div class="section">
    <h2>👥 Participants</h2>
    <div class="participant p1">
      <div class="label">Person 1:</div>
      <div class="value ${!sessionData.participants.person1 ? 'empty' : ''}">${sessionData.participants.person1 || '(Not specified)'}</div>
    </div>
    <div class="participant p2">
      <div class="label">Person 2:</div>
      <div class="value ${!sessionData.participants.person2 ? 'empty' : ''}">${sessionData.participants.person2 || '(Not specified)'}</div>
    </div>
    <div class="label">Witnesses:</div>
    <div class="value ${!sessionData.witnesses ? 'empty' : ''}">${sessionData.witnesses || '(None listed)'}</div>
  </div>

  <div class="section">
    <h2>👁️ Observations (Without Judgment)</h2>
    <div class="participant p1">
      <div class="label">${sessionData.participants.person1 || 'Person 1'}'s Observations:</div>
      <div class="value ${!sessionData.observations.person1 ? 'empty' : ''}">${sessionData.observations.person1 || '(Not recorded)'}</div>
    </div>
    <div class="participant p2">
      <div class="label">${sessionData.participants.person2 || 'Person 2'}'s Observations:</div>
      <div class="value ${!sessionData.observations.person2 ? 'empty' : ''}">${sessionData.observations.person2 || '(Not recorded)'}</div>
    </div>
  </div>

  <div class="section">
    <h2>🌱 The Green: Needs</h2>
    <div class="participant p1">
      <div class="label">${sessionData.participants.person1 || 'Person 1'}'s Needs:</div>
      <div class="value ${!sessionData.needs.person1.filter(n => n).length ? 'empty' : ''}">${sessionData.needs.person1.filter(n => n).join(', ') || '(Not identified)'}</div>
    </div>
    <div class="participant p2">
      <div class="label">${sessionData.participants.person2 || 'Person 2'}'s Needs:</div>
      <div class="value ${!sessionData.needs.person2.filter(n => n).length ? 'empty' : ''}">${sessionData.needs.person2.filter(n => n).join(', ') || '(Not identified)'}</div>
    </div>
  </div>

  <div class="section">
    <h2>❤️ The Red: Feelings</h2>
    <div class="participant p1">
      <div class="label">${sessionData.participants.person1 || 'Person 1'} - When Needs Met:</div>
      <div class="value ${!sessionData.feelings.person1.met ? 'empty' : ''}">${sessionData.feelings.person1.met || '(Not recorded)'}</div>
      <div class="label">${sessionData.participants.person1 || 'Person 1'} - When Needs NOT Met:</div>
      <div class="value ${!sessionData.feelings.person1.unmet ? 'empty' : ''}">${sessionData.feelings.person1.unmet || '(Not recorded)'}</div>
    </div>
    <div class="participant p2">
      <div class="label">${sessionData.participants.person2 || 'Person 2'} - When Needs Met:</div>
      <div class="value ${!sessionData.feelings.person2.met ? 'empty' : ''}">${sessionData.feelings.person2.met || '(Not recorded)'}</div>
      <div class="label">${sessionData.participants.person2 || 'Person 2'} - When Needs NOT Met:</div>
      <div class="value ${!sessionData.feelings.person2.unmet ? 'empty' : ''}">${sessionData.feelings.person2.unmet || '(Not recorded)'}</div>
    </div>
  </div>

  <div class="gold">
    <h3>🏆 The Gold: Mutual Strategy</h3>
    <div class="value ${!sessionData.strategies.mutual ? 'empty' : ''}" style="min-height: 50px;">${sessionData.strategies.mutual || '(Not identified)'}</div>
  </div>

  <div class="section">
    <h2>✨ Celebration & Insights</h2>
    <div class="label">Celebration:</div>
    <div class="value ${!sessionData.celebration ? 'empty' : ''}">${sessionData.celebration || '(Not recorded)'}</div>
    <div class="label">Insights:</div>
    <div class="value ${!sessionData.insights ? 'empty' : ''}">${sessionData.insights || '(Not recorded)'}</div>
  </div>

  <div class="footer">
    <p>Generated from <strong>Belonging Sessions</strong> E-book</p>
    <p>"Stay in the love and stay in the vulnerability. The vulnerability leads to potency."</p>
  </div>
</body>
</html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => printWindow.print(), 250);
    };
    toast.success('PDF download started!');
  };

  // Toggle presentation mode
  const togglePresentationMode = async () => {
    if (!isPresentationMode) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) { console.log('Fullscreen not available'); }
    } else {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch (e) { console.log('Exit fullscreen failed'); }
    }
    setIsPresentationMode(!isPresentationMode);
  };

  // Pulsing Feelings & Needs CTA
  const FeelingsNeedsCTA = ({ className = '' }) => (
    <Button
      onClick={() => setIsHelperOpen(true)}
      className={`relative bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white ${className}`}
      data-testid="feelings-needs-cta"
    >
      <div className="absolute inset-0 rounded-md bg-rose-500/30 animate-ping" style={{ animationDuration: '2s' }} />
      <span className="relative flex items-center gap-2">
        <FeelIcon size={18} />
        Feelings & Needs Tool
        <NeedIcon size={18} />
      </span>
    </Button>
  );

  // Fishbowl Visualization
  const FishbowlVisualization = () => (
    <div className="relative w-full max-w-xl mx-auto aspect-square">
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-amber-500/20 rounded-full blur-3xl animate-pulse" />
      
      <svg viewBox="0 0 400 400" className="w-full h-full relative z-10">
        {/* Outer circle - Witnesses */}
        <circle cx="200" cy="200" r="180" fill="none" stroke="url(#witnessGradient)" strokeWidth="20" opacity="0.3" />
        <circle cx="200" cy="200" r="180" fill="none" stroke="url(#witnessGradient)" strokeWidth="2" strokeDasharray="10,5" />
        
        {/* Fishbowl (inner circle) */}
        <ellipse cx="200" cy="200" rx="120" ry="100" fill="url(#bowlGradient)" opacity="0.2" />
        <ellipse cx="200" cy="200" rx="120" ry="100" fill="none" stroke="url(#bowlStroke)" strokeWidth="3" />
        
        {/* Person 1 */}
        <circle cx="140" cy="200" r="35" fill="url(#person1Gradient)" />
        <text x="140" y="200" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" dy="3">Person 1</text>
        
        {/* Person 2 */}
        <circle cx="260" cy="200" r="35" fill="url(#person2Gradient)" />
        <text x="260" y="200" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" dy="3">Person 2</text>
        
        {/* Connection heart */}
        <text x="200" y="205" textAnchor="middle" fontSize="24">💚</text>
        
        {/* Witness icons around perimeter */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
          const rad = (angle * Math.PI) / 180;
          const x = 200 + 165 * Math.cos(rad);
          const y = 200 + 165 * Math.sin(rad);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="15" fill="#4B5563" opacity="0.8" />
              <text x={x} y={y + 4} textAnchor="middle" fill="white" fontSize="10">👤</text>
            </g>
          );
        })}
        
        {/* Labels */}
        <text x="200" y="30" textAnchor="middle" fill="#9CA3AF" fontSize="12" fontWeight="bold">WITNESSES / SUPPORTERS</text>
        <text x="200" y="370" textAnchor="middle" fill="#9CA3AF" fontSize="11">Holding Space with Love & Compassion</text>
        
        {/* Gradients */}
        <defs>
          <linearGradient id="witnessGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6B7280" />
            <stop offset="100%" stopColor="#4B5563" />
          </linearGradient>
          <radialGradient id="bowlGradient" cx="50%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="100%" stopColor="#3B82F6" />
          </radialGradient>
          <linearGradient id="bowlStroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
          <radialGradient id="person1Gradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#34D399" />
            <stop offset="100%" stopColor="#22C55E" />
          </radialGradient>
          <radialGradient id="person2Gradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </radialGradient>
        </defs>
      </svg>
      
      {/* Floating elements */}
      <div className="absolute top-5 left-5 text-xl animate-bounce">⚗️</div>
      <div className="absolute top-5 right-5 text-xl animate-bounce" style={{ animationDelay: '0.5s' }}>✨</div>
      <div className="absolute bottom-20 left-10 text-xl animate-bounce" style={{ animationDelay: '1s' }}>🌱</div>
      <div className="absolute bottom-20 right-10 text-xl animate-bounce" style={{ animationDelay: '1.5s' }}>🏆</div>
    </div>
  );

  const renderContent = () => {
    switch (currentSection) {
      // INTRO
      case 0:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="alchemy-intro">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-purple-500/30">
                <span className="text-5xl">⚗️</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-3">Empathetic Alchemy</h2>
              <p className="text-xl text-purple-300">The Fishbowl Process • 15-60 minutes</p>
            </div>

            <FishbowlVisualization />

            <div className="flex justify-center">
              <FeelingsNeedsCTA className="px-6 py-3 text-lg" />
            </div>

            {/* Key Concept */}
            <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/30 border border-purple-500/40 rounded-2xl p-8">
              <p className="text-xl text-purple-100 leading-relaxed text-center">
                Two people in the middle talk while <span className="text-purple-300 font-bold">supportive witnesses</span> hold 
                space around the perimeter. Together, transform <span className="text-rose-300 font-bold">conflict</span> into 
                <span className="text-amber-300 font-bold"> connection</span>.
              </p>
            </div>

            {/* The Alchemy */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-emerald-900/30 border border-emerald-500/40 rounded-xl p-5 text-center">
                <span className="text-3xl mb-3 block">🌱</span>
                <h3 className="text-emerald-300 font-bold mb-2">The Green</h3>
                <p className="text-slate-300 text-sm">Needs - The elemental requirements of life</p>
              </div>
              <div className="bg-rose-900/30 border border-rose-500/40 rounded-xl p-5 text-center">
                <span className="text-3xl mb-3 block">❤️</span>
                <h3 className="text-rose-300 font-bold mb-2">The Red</h3>
                <p className="text-slate-300 text-sm">Feelings - The energy guiding us</p>
              </div>
              <div className="bg-amber-900/30 border border-amber-500/40 rounded-xl p-5 text-center">
                <span className="text-3xl mb-3 block">🏆</span>
                <h3 className="text-amber-300 font-bold mb-2">The Gold</h3>
                <p className="text-slate-300 text-sm">Strategies that heal & bind us together</p>
              </div>
            </div>

            {/* Big Quote */}
            <div className="bg-gradient-to-r from-pink-900/40 to-purple-900/30 border-2 border-pink-500/50 rounded-2xl p-6 text-center">
              <p className="text-xl text-pink-100 italic">
                "Stay in the love and stay in the vulnerability. 
                <span className="text-pink-300 font-bold"> The vulnerability leads to potency</span> — paradoxically!"
              </p>
            </div>

            {/* ElchmE Callout */}
            <ElchmEFishbowlCallout />
          </div>
        );

      // SETUP
      case 1:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="alchemy-setup">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-3">Setup the Fishbowl</h2>
              <p className="text-blue-300">Prepare the sacred space</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            {/* Participants */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/20 border-2 border-emerald-500/40 rounded-xl p-5">
                <label className="text-lg font-bold text-emerald-300 mb-3 block flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">1</span>
                  Person 1 (in the bowl)
                </label>
                <Input
                  value={sessionData.participants.person1}
                  onChange={(e) => setSessionData(prev => ({ 
                    ...prev, 
                    participants: { ...prev.participants, person1: e.target.value } 
                  }))}
                  placeholder="Name..."
                  className="bg-emerald-900/20 border-emerald-700/30 text-white"
                />
              </div>

              <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border-2 border-purple-500/40 rounded-xl p-5">
                <label className="text-lg font-bold text-purple-300 mb-3 block flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">2</span>
                  Person 2 (in the bowl)
                </label>
                <Input
                  value={sessionData.participants.person2}
                  onChange={(e) => setSessionData(prev => ({ 
                    ...prev, 
                    participants: { ...prev.participants, person2: e.target.value } 
                  }))}
                  placeholder="Name..."
                  className="bg-purple-900/20 border-purple-700/30 text-white"
                />
              </div>
            </div>

            {/* Witnesses */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <label className="text-lg font-bold text-slate-200 mb-3 block flex items-center gap-2">
                <PartnerIcon size={20} />
                Witnesses / Supporters (around the perimeter)
              </label>
              <Textarea
                value={sessionData.witnesses}
                onChange={(e) => setSessionData(prev => ({ ...prev, witnesses: e.target.value }))}
                placeholder="Who is holding space? Counselor, friends, family..."
                className="bg-slate-900/50 border-slate-600 text-white min-h-[80px]"
              />
            </div>

            {/* Guidelines */}
            <div className="bg-teal-900/30 border border-teal-500/30 rounded-xl p-5">
              <h3 className="text-teal-300 font-bold mb-3 flex items-center gap-2">
                <InsightIcon size={20} color="#14B8A6" />
                Ground Rules
              </h3>
              <ul className="space-y-2 text-slate-200">
                <li className="flex items-start gap-2">
                  <span className="text-teal-400">•</span>
                  <span>Witnesses hold space silently, offering only loving presence</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-400">•</span>
                  <span>No interrupting the people in the bowl</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-400">•</span>
                  <span>Everything shared stays confidential</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-400">•</span>
                  <span>Stay in radical tenderness — no judgment</span>
                </li>
              </ul>
            </div>

            {/* Insight */}
            <div className="bg-pink-900/30 border border-pink-500/30 rounded-xl p-5">
              <p className="text-pink-200 italic text-center">
                💗 "Don't make people do anything. Don't wait for them to be a certain way. 
                <span className="text-pink-300 font-semibold">Change yourself to your receptivity of love and understanding firstly</span>, 
                and then delineate your needs."
              </p>
            </div>
          </div>
        );

      // PROCESS OVERVIEW
      case 2:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="alchemy-process">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-3">The Alchemy Process</h2>
              <p className="text-purple-300">15 minutes to 1 hour • Follow these steps</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            {/* Process Steps */}
            <div className="space-y-4">
              {processSteps.map((step, idx) => {
                const colorClasses = {
                  blue: 'from-blue-900/40 to-indigo-900/30 border-blue-500/40',
                  slate: 'from-slate-800/40 to-slate-900/30 border-slate-500/40',
                  emerald: 'from-emerald-900/40 to-green-900/30 border-emerald-500/40',
                  rose: 'from-rose-900/40 to-red-900/30 border-rose-500/40',
                  amber: 'from-amber-900/40 to-orange-900/30 border-amber-500/40',
                  purple: 'from-purple-900/40 to-pink-900/30 border-purple-500/40',
                };
                const textColors = {
                  blue: 'text-blue-300',
                  slate: 'text-slate-300',
                  emerald: 'text-emerald-300',
                  rose: 'text-rose-300',
                  amber: 'text-amber-300',
                  purple: 'text-purple-300',
                };
                
                return (
                  <div
                    key={step.id}
                    className={`bg-gradient-to-br ${colorClasses[step.color]} border rounded-xl p-5 cursor-pointer transition-all hover:scale-[1.01] ${activeStep === idx ? 'ring-2 ring-white/50' : ''}`}
                    onClick={() => setActiveStep(idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-800/80 rounded-full flex items-center justify-center">
                          <span className="text-2xl">{step.icon}</span>
                        </div>
                        <div>
                          <h3 className={`font-bold text-lg ${textColors[step.color]}`}>
                            {idx + 1}. {step.title}
                          </h3>
                          <p className="text-slate-300 text-sm">{step.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-slate-400">
                        <TimerIcon size={16} color="#94A3B8" />
                          <span className="text-sm">{step.duration}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total Time */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
              <p className="text-slate-300">
                <span className="text-amber-400 font-bold">Total Session Time:</span> 15-60 minutes
              </p>
              <p className="text-slate-400 text-sm mt-1">
                Don't leave it vague and open-ended. Use this process to solve problems and celebrate wins!
              </p>
            </div>
          </div>
        );

      // LIVE SESSION
      case 3:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="alchemy-session">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Live Session</h2>
              <div className="flex items-center gap-4">
                {/* Timer */}
                <div className="bg-slate-800 rounded-lg px-4 py-2 flex items-center gap-2">
                  <TimerIcon size={20} />
                  <span className="text-xl font-mono text-white">{formatTime(timerSeconds)}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setTimerRunning(!timerRunning)}
                    className={timerRunning ? 'text-red-400' : 'text-emerald-400'}
                  >
                    {timerRunning ? 'Pause' : 'Start'}
                  </Button>
                </div>
                <FeelingsNeedsCTA />
              </div>
            </div>

            {/* Observations */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <h3 className="text-lg font-bold text-slate-200 mb-4 flex items-center gap-2">
                <ObserveIcon size={20} />
                Step 1: Observations (No Judgment)
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-emerald-300 mb-2 block">
                    {sessionData.participants.person1 || 'Person 1'}'s observations:
                  </label>
                  <Textarea
                    value={sessionData.observations.person1}
                    onChange={(e) => setSessionData(prev => ({ 
                      ...prev, 
                      observations: { ...prev.observations, person1: e.target.value } 
                    }))}
                    placeholder="What happened? Just the facts..."
                    className="bg-emerald-900/20 border-emerald-700/30 text-white min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-purple-300 mb-2 block">
                    {sessionData.participants.person2 || 'Person 2'}'s observations:
                  </label>
                  <Textarea
                    value={sessionData.observations.person2}
                    onChange={(e) => setSessionData(prev => ({ 
                      ...prev, 
                      observations: { ...prev.observations, person2: e.target.value } 
                    }))}
                    placeholder="What happened? Just the facts..."
                    className="bg-purple-900/20 border-purple-700/30 text-white min-h-[80px]"
                  />
                </div>
              </div>
            </div>

            {/* Needs - The Green */}
            <div className="bg-emerald-900/30 border border-emerald-500/40 rounded-xl p-5">
              <h3 className="text-lg font-bold text-emerald-300 mb-4">🌱 Step 2: The Green (Needs)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-emerald-200 mb-2 block">
                    {sessionData.participants.person1 || 'Person 1'}'s needs:
                  </label>
                  {sessionData.needs.person1.map((need, idx) => (
                    <Input
                      key={idx}
                      value={need}
                      onChange={(e) => {
                        const newNeeds = [...sessionData.needs.person1];
                        newNeeds[idx] = e.target.value;
                        setSessionData(prev => ({ 
                          ...prev, 
                          needs: { ...prev.needs, person1: newNeeds } 
                        }));
                      }}
                      placeholder={`Need ${idx + 1}...`}
                      className="bg-emerald-900/20 border-emerald-700/30 text-white mb-2"
                    />
                  ))}
                </div>
                <div>
                  <label className="text-sm font-medium text-purple-200 mb-2 block">
                    {sessionData.participants.person2 || 'Person 2'}'s needs:
                  </label>
                  {sessionData.needs.person2.map((need, idx) => (
                    <Input
                      key={idx}
                      value={need}
                      onChange={(e) => {
                        const newNeeds = [...sessionData.needs.person2];
                        newNeeds[idx] = e.target.value;
                        setSessionData(prev => ({ 
                          ...prev, 
                          needs: { ...prev.needs, person2: newNeeds } 
                        }));
                      }}
                      placeholder={`Need ${idx + 1}...`}
                      className="bg-purple-900/20 border-purple-700/30 text-white mb-2"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Feelings - The Red */}
            <div className="bg-rose-900/30 border border-rose-500/40 rounded-xl p-5">
              <h3 className="text-lg font-bold text-rose-300 mb-4">❤️ Step 3: The Red (Feelings)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-amber-300 mb-2 block">
                      {sessionData.participants.person1 || 'Person 1'} when needs MET:
                    </label>
                    <Textarea
                      value={sessionData.feelings.person1.met}
                      onChange={(e) => setSessionData(prev => ({ 
                        ...prev, 
                        feelings: { ...prev.feelings, person1: { ...prev.feelings.person1, met: e.target.value } } 
                      }))}
                      placeholder="Relieved, grateful, hopeful..."
                      className="bg-amber-900/20 border-amber-700/30 text-white min-h-[60px]"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-rose-300 mb-2 block">
                      {sessionData.participants.person1 || 'Person 1'} when needs NOT met:
                    </label>
                    <Textarea
                      value={sessionData.feelings.person1.unmet}
                      onChange={(e) => setSessionData(prev => ({ 
                        ...prev, 
                        feelings: { ...prev.feelings, person1: { ...prev.feelings.person1, unmet: e.target.value } } 
                      }))}
                      placeholder="Frustrated, hurt, anxious..."
                      className="bg-rose-900/20 border-rose-700/30 text-white min-h-[60px]"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-amber-300 mb-2 block">
                      {sessionData.participants.person2 || 'Person 2'} when needs MET:
                    </label>
                    <Textarea
                      value={sessionData.feelings.person2.met}
                      onChange={(e) => setSessionData(prev => ({ 
                        ...prev, 
                        feelings: { ...prev.feelings, person2: { ...prev.feelings.person2, met: e.target.value } } 
                      }))}
                      placeholder="Relieved, grateful, hopeful..."
                      className="bg-amber-900/20 border-amber-700/30 text-white min-h-[60px]"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-rose-300 mb-2 block">
                      {sessionData.participants.person2 || 'Person 2'} when needs NOT met:
                    </label>
                    <Textarea
                      value={sessionData.feelings.person2.unmet}
                      onChange={(e) => setSessionData(prev => ({ 
                        ...prev, 
                        feelings: { ...prev.feelings, person2: { ...prev.feelings.person2, unmet: e.target.value } } 
                      }))}
                      placeholder="Frustrated, hurt, anxious..."
                      className="bg-rose-900/20 border-rose-700/30 text-white min-h-[60px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* The Gold - Mutual Strategy */}
            <div className="bg-gradient-to-br from-amber-900/40 to-yellow-900/30 border-2 border-amber-500/50 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl" />
              <div className="relative">
                <h3 className="text-xl font-bold text-amber-300 mb-4 flex items-center gap-2">
                  🏆 Step 4: The Gold (Mutual Strategy)
                </h3>
                <p className="text-amber-200/70 text-sm mb-4">
                  Work through this until there is the most minimum solution. This is the gold!
                </p>
                <Textarea
                  value={sessionData.strategies.mutual}
                  onChange={(e) => setSessionData(prev => ({ 
                    ...prev, 
                    strategies: { ...prev.strategies, mutual: e.target.value } 
                  }))}
                  placeholder="What strategy honors BOTH sets of needs? What can we both say yes to?"
                  className="bg-amber-900/30 border-amber-500/40 text-white min-h-[120px] text-lg"
                />
              </div>
            </div>
          </div>
        );

      // CELEBRATION
      case 4:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="alchemy-closure">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-purple-500/30">
                <SparkleIcon size={40} color="white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Celebration & Closure</h2>
              <p className="text-purple-300">Honor the courage and connection</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            {/* Celebration */}
            <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/30 border border-purple-500/40 rounded-xl p-6">
              <label className="text-lg font-bold text-purple-300 mb-3 block flex items-center gap-2">
                ✨ What Are You Celebrating?
              </label>
              <Textarea
                value={sessionData.celebration}
                onChange={(e) => setSessionData(prev => ({ ...prev, celebration: e.target.value }))}
                placeholder="The courage shown... The connection made... The insights gained... The love expressed..."
                className="bg-purple-900/20 border-purple-700/30 text-white min-h-[100px]"
              />
            </div>

            {/* Insights */}
            <div className="bg-gradient-to-br from-teal-900/40 to-emerald-900/30 border border-teal-500/40 rounded-xl p-6">
              <label className="text-lg font-bold text-teal-300 mb-3 block flex items-center gap-2">
                <InsightIcon size={24} color="#2DD4BF" />
                Key Insights & Takeaways
              </label>
              <Textarea
                value={sessionData.insights}
                onChange={(e) => setSessionData(prev => ({ ...prev, insights: e.target.value }))}
                placeholder="What did we learn? What will we do differently? What opened up?"
                className="bg-teal-900/20 border-teal-700/30 text-white min-h-[100px]"
              />
            </div>

            {/* Alchemy Insights Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              {alchemyInsights.map((insight) => (
                <div key={insight.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{insight.icon}</span>
                    <h4 className="text-white font-bold">{insight.title}</h4>
                  </div>
                  <p className="text-slate-300 text-sm">{insight.content}</p>
                </div>
              ))}
            </div>

            {/* Final Quote */}
            <div className="bg-gradient-to-r from-amber-900/40 to-yellow-900/30 border-2 border-amber-500/50 rounded-2xl p-6 text-center">
              <p className="text-xl text-amber-100 leading-relaxed">
                The <span className="text-emerald-300 font-bold">green</span> of our needs + the <span className="text-rose-300 font-bold">red</span> of our feelings 
                = the alchemy to <span className="text-amber-300 font-bold">Gold</span> — strategies that heal, strategies that work, 
                strategies that bind us together <span className="text-amber-200">ripping through the ages</span> beyond what we can even comprehend.
              </p>
            </div>

            {/* Completion */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300 text-lg mb-4">
                You've completed the <span className="text-purple-400 font-bold">Empathetic Alchemy</span> session! ⚗️✨
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => navigate('/workbook/compassionate-wheel-enhanced')}
                  variant="outline"
                  className="border-emerald-600 text-emerald-300 hover:bg-emerald-900/30"
                >
                  <ArrowLeftIcon size={16} className="mr-2" />
                  ← Communication Wheel
                </Button>
                <Button
                  onClick={() => navigate('/belonging-ebook')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                >
                  <ChapterIcon size={18} className="mr-2" />
                  Return to eBook
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${isPresentationMode ? 'overflow-hidden' : ''}`}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeftIcon size={16} className="mr-1" />
                Back
              </Button>
              <div className="h-5 w-px bg-slate-700" />
              <div>
                <h1 className="text-lg font-bold text-white">Empathetic Alchemy</h1>
                <p className="text-xs text-purple-400">Fishbowl Process • 15-60 min</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button onClick={saveProgress} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <SaveIcon size={16} className="mr-1" />
                Save
              </Button>
              <Button onClick={downloadPDF} variant="ghost" size="sm" className="text-slate-400 hover:text-white" title="Download as PDF">
                <DownloadIcon size={16} className="mr-1" />
                PDF
              </Button>
              <Button onClick={resetAll} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ResetIcon size={16} />
              </Button>
              <Button onClick={togglePresentationMode} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                {isPresentationMode ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress value={progress_pct} className="h-1.5 bg-slate-700" />
            <div className="flex justify-between mt-1 text-xs text-slate-500">
              <span>Section {currentSection + 1} of {sections.length}</span>
              <span>Page {sections[currentSection]?.page}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Section Navigation */}
      {!isPresentationMode && (
        <div className="bg-slate-800/50 border-b border-slate-700/30">
          <div className="max-w-5xl mx-auto px-4 py-2">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {sections.map((section, idx) => (
                <button
                  key={section.id}
                  onClick={() => setCurrentSection(idx)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${
                    currentSection === idx
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-slate-200'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`max-w-4xl mx-auto px-4 py-8 ${isPresentationMode ? 'h-screen flex items-center' : ''}`}>
        <div className="w-full">
          {renderContent()}
        </div>
      </main>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-700/50 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            onClick={() => setCurrentSection(prev => Math.max(prev - 1, 0))}
            disabled={currentSection === 0}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-30"
          >
            <ArrowLeftIcon size={18} className="mr-2" />
            Previous
          </Button>
          
          <div className="text-center">
            <span className="text-slate-400 text-sm">{sections[currentSection]?.title}</span>
          </div>
          
          <Button
            onClick={() => setCurrentSection(prev => Math.min(prev + 1, sections.length - 1))}
            disabled={currentSection === sections.length - 1}
            className="bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-30"
          >
            Next
            <ArrowRightIcon size={18} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Floating Feelings & Needs Helper */}
      <FloatingFeelingsHelper />

      {/* Feelings & Needs Helper Modal */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
